import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '../components/ui/Dialog';
import { gameAPI } from '../services/api';
import { useGameStore } from '../store/gameStore';
import { getApiUrl } from '../utils/api-url';
import { useIsMobile } from '../hooks/useIsMobile';

// A player's games: ones they created and ones they were invited to (joined).
export default function MyGames() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [created, setCreated] = useState<any[]>([]);
  const [invited, setInvited] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [confirmGame, setConfirmGame] = useState<any | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const { setCurrentGame, setSetupStep, recentGames } = useGameStore();

  useEffect(() => {
    (async () => {
      try {
        const createdIds = new Set<string>();
        const invitedIds = new Set<string>();

        // 1) Games remembered by this browser (reliable across sessions) → created by me.
        for (const g of recentGames || []) if (g?.id) createdIds.add(g.id);

        // 2) Server session games → created by me.
        try {
          const s = await gameAPI.getSessionGames();
          for (const g of s.games || []) createdIds.add(g.id);
        } catch (e) { console.error('session games:', e); }

        // 3) Account-linked games (by role), for logged-in users / cross-device.
        const userDataStr = localStorage.getItem('userData');
        const userId = userDataStr ? JSON.parse(userDataStr)?.user?.id : null;
        if (userId) {
          try {
            const resp = await gameAPI.getUserGames(userId);
            for (const g of resp.games || []) {
              if (g.user_role === 'creator') createdIds.add(g.id);
              else invitedIds.add(g.id);
            }
          } catch (e) { console.error('user games:', e); }
        }
        for (const id of createdIds) invitedIds.delete(id);

        // Backfill account links so these games persist to the user (and show cross-device).
        if (userId) {
          for (const id of createdIds) gameAPI.claimGame(id, userId, 'creator').catch(() => {});
        }

        // Fetch fresh data for each game (current status / player count); drop deleted ones.
        const fetchFresh = async (ids: Set<string>) => {
          const results = await Promise.all(
            [...ids].map((id) => gameAPI.getGame(id).then((g: any) => g).catch(() => null)),
          );
          return results.filter(Boolean);
        };

        setCreated(await fetchFresh(createdIds));
        setInvited(await fetchFresh(invitedIds));
      } finally {
        setIsLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openGame = async (game: any) => {
    setCurrentGame(game);
    if (game.status === 'setup') {
      setSetupStep('add-players');
      window.location.href = '/';
      return;
    }
    const playerId = localStorage.getItem(`game_${game.id}_player`);
    const url = playerId ? `/game/${game.id}?playerId=${playerId}` : `/game/${game.id}`;

    // Already started (has at least one turn) → straight into the game, skip the intro.
    if ((game.current_turn_number || 0) > 0) {
      window.location.href = url;
      return;
    }
    // Not started yet → show the intro the first time, if one exists.
    try {
      const apiUrl = getApiUrl();
      const r = await fetch(`${apiUrl}/api/games/${game.id}/intro`);
      window.location.href = r.ok ? `/intro/${game.id}` : url;
    } catch {
      window.location.href = url;
    }
  };

  const apiUrl = getApiUrl();
  const userId: string | null = (() => {
    try { return JSON.parse(localStorage.getItem('userData') || 'null')?.user?.id || null; } catch { return null; }
  })();

  const togglePause = async (game: any) => {
    const paused = game.status !== 'paused';
    try {
      await gameAPI.pauseGame(game.id, userId || '', paused);
      setCreated((cs) => cs.map((g) => (g.id === game.id ? { ...g, status: paused ? 'paused' : 'active' } : g)));
    } catch (e: any) {
      alert(t('myGames.pauseFailed') + ': ' + (e?.response?.data?.error || e?.message));
    }
  };

  const copyInvite = (game: any) => {
    const url = `${window.location.origin}/join/${game.share_code}`;
    navigator.clipboard.writeText(url);
    setCopiedId(game.id);
    setTimeout(() => setCopiedId((c) => (c === game.id ? null : c)), 2000);
  };

  const removeGame = (game: any) => setConfirmGame(game);

  const doDelete = async () => {
    const game = confirmGame;
    if (!game) return;
    setDeleting(true);
    try {
      await gameAPI.deleteGame(game.id, userId || '');
      setCreated((cs) => cs.filter((g) => g.id !== game.id));
      setConfirmGame(null);
    } catch (e: any) {
      alert(t('myGames.deleteFailed') + ': ' + (e?.response?.data?.error || e?.message));
    } finally {
      setDeleting(false);
    }
  };

  const iconBtn = (bg: string): React.CSSProperties => ({
    padding: '0.5rem 0.7rem', fontSize: '1rem', border: 'none', borderRadius: '10px',
    background: bg, color: '#fff', cursor: 'pointer', flexShrink: 0,
  });

  const Card = ({ game, owned = false }: { game: any; owned?: boolean }) => (
    <div
      onClick={() => openGame(game)}
      style={{
        padding: isMobile ? '1rem' : '1.25rem 1.5rem', background: 'rgba(0,0,0,0.3)', borderRadius: '12px',
        border: `1px solid ${game.status === 'paused' ? 'rgba(234,179,8,0.35)' : 'rgba(99,102,241,0.2)'}`,
        display: 'flex', flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', cursor: 'pointer',
        transition: 'all 0.2s', gap: isMobile ? '0.75rem' : '1rem',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateX(4px)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateX(0)'; }}
    >
      <div style={{ display: 'flex', gap: '0.9rem', alignItems: 'center', minWidth: 0 }}>
        <div style={{
          width: isMobile ? 76 : 120, height: isMobile ? 76 : 120, borderRadius: '12px', overflow: 'hidden', flexShrink: 0,
          background: 'linear-gradient(135deg, #3a2a5a, #241833)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(99,102,241,0.25)',
        }}>
          {game?.metadata?.intro?.imageId ? (
            <img src={`${apiUrl}/api/media/${game.metadata.intro.imageId}`} alt="" loading="lazy"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          ) : (
            <span style={{ fontSize: '2.5rem' }}>🎲</span>
          )}
        </div>
        <div style={{ minWidth: 0 }}>
          <h3 style={{ fontSize: '1.15rem', marginBottom: '0.4rem', color: '#e2e8f0' }}>{game.name}</h3>
          <div style={{ fontSize: '0.9rem', color: '#a0aec0', display: 'flex', gap: '1.25rem', flexWrap: 'wrap' }}>
            <span>🌍 {game.universe}</span>
            <span>👥 {game.players_count || 0}</span>
            <span>
              {game.status === 'setup' && `⏳ ${t('myGames.statusSetup')}`}
              {game.status === 'active' && `🎮 ${t('myGames.statusActive')}`}
              {game.status === 'paused' && `⏸ ${t('myGames.statusPaused')}`}
              {game.status === 'completed' && `✅ ${t('myGames.statusCompleted')}`}
            </span>
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexShrink: 0, flexWrap: 'wrap', justifyContent: isMobile ? 'flex-end' : undefined }}>
        {owned && game.share_code && (game.max_players || 1) > 1 && (
          <button
            title={t('myGames.copyInviteTitle')}
            onClick={(e) => { e.stopPropagation(); copyInvite(game); }}
            style={iconBtn(copiedId === game.id ? 'rgba(16,185,129,0.35)' : 'rgba(16,185,129,0.2)')}
          >
            {copiedId === game.id ? '✓' : '🔗'}
          </button>
        )}
        {owned && (game.players_count || 0) > 1 && (game.status === 'active' || game.status === 'paused') && (
          <button
            title={game.status === 'paused' ? t('myGames.resume') : t('myGames.pause')}
            onClick={(e) => { e.stopPropagation(); togglePause(game); }}
            style={iconBtn('rgba(234,179,8,0.25)')}
          >
            {game.status === 'paused' ? '▶' : '⏸'}
          </button>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); openGame(game); }}
          style={{
            padding: '0.6rem 1.1rem', fontSize: '0.95rem', fontWeight: 600, border: 'none',
            borderRadius: '10px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap',
          }}
        >
          {game.status === 'setup'
            ? t('myGames.statusSetup')
            : (game.current_turn_number || 0) > 0 ? t('myGames.continueGame') : t('myGames.open')} →
        </button>
        {owned && (
          <button
            title={t('myGames.deleteGameTitle')}
            onClick={(e) => { e.stopPropagation(); removeGame(game); }}
            style={iconBtn('rgba(239,68,68,0.25)')}
          >
            🗑
          </button>
        )}
      </div>
    </div>
  );

  const Section = ({ title, icon, games, empty, owned = false }: { title: string; icon: string; games: any[]; empty: string; owned?: boolean }) => (
    <div style={{ marginBottom: '2rem' }}>
      <h2 style={{ fontSize: isMobile ? '1.15rem' : '1.4rem', color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1rem' }}>
        <span style={{ fontSize: '1.6rem' }}>{icon}</span> {title}
        <span style={{ fontSize: '0.9rem', color: '#a0aec0', fontWeight: 400 }}>({games.length})</span>
      </h2>
      {games.length === 0 ? (
        <p style={{ color: '#a0aec0' }}>{empty}</p>
      ) : (
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          {games.map((g) => <Card key={g.id} game={g} owned={owned} />)}
        </div>
      )}
    </div>
  );

  return (
    <div style={{ maxWidth: '900px', margin: '1rem auto 4rem auto', padding: '0 1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: isMobile ? '1.5rem' : '2rem', fontWeight: 'bold', margin: 0,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          📂 {t('nav.myGames')}
        </h1>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button
            onClick={() => { window.location.href = '/?create=1'; }}
            style={{ padding: '0.5rem 1rem', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              border: 'none', borderRadius: '20px', color: '#fff', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer' }}
          >
            ✨ {t('home.create_game')}
          </button>
          <button
            onClick={() => { window.location.href = '/'; }}
            style={{ padding: '0.5rem 1rem', background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.35)',
              borderRadius: '20px', color: '#c7d2fe', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer' }}
          >
            🏠 {t('nav.home')}
          </button>
        </div>
      </div>

      {isLoading ? (
        <p style={{ textAlign: 'center', color: '#a0aec0' }}>{t('common.loading')}</p>
      ) : (
        <div style={{ background: 'rgba(15,12,41,0.6)', backdropFilter: 'blur(20px)', borderRadius: '20px',
          border: '1px solid rgba(99,102,241,0.2)', padding: isMobile ? '1.25rem' : '2rem' }}>
          <Section title={t('myGames.createdByMe')} icon="🛠️" games={created} owned
            empty={t('myGames.createdEmpty')} />
          <Section title={t('myGames.invited')} icon="✉️" games={invited}
            empty={t('myGames.invitedEmpty')} />
        </div>
      )}

      {/* Delete confirmation. Was a bare fixed div: focus stayed on the tiny bin icon behind
          the overlay, Escape did nothing, and the game list underneath stayed in the reading
          order — an irreversible delete confirmed blind. */}
      {confirmGame && (
        <Dialog
          title={t('myGames.deleteConfirmTitle')}
          onClose={() => setConfirmGame(null)}
          busy={deleting}
          maxWidth="420px"
          padding="2rem"
        >
          <div style={{ textAlign: 'center' }}>
            <div aria-hidden="true" style={{
              width: 64, height: 64, margin: '0 auto 1rem', borderRadius: '50%',
              background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem',
            }}>🗑️</div>
            <p style={{ color: '#cbd5e0', fontSize: '1rem', margin: '0 0 0.4rem' }}>
              «{confirmGame.name}»
            </p>
            <p style={{ color: '#a0aec0', fontSize: '0.9rem', margin: '0 0 1.5rem', lineHeight: 1.5 }}>
              {t('myGames.deleteWarning')}
            </p>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                type="button"
                onClick={() => setConfirmGame(null)}
                disabled={deleting}
                style={{
                  flex: 1, padding: '0.85rem', borderRadius: '12px', fontSize: '1rem', fontWeight: 600,
                  border: '1px solid rgba(99,102,241,0.35)', background: 'rgba(99,102,241,0.12)',
                  color: '#c7d2fe', cursor: deleting ? 'default' : 'pointer',
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={doDelete}
                disabled={deleting}
                aria-label={`${t('common.delete')}: ${confirmGame.name}`}
                style={{
                  flex: 1, padding: '0.85rem', borderRadius: '12px', fontSize: '1rem', fontWeight: 700,
                  border: 'none', background: 'linear-gradient(135deg, #ef4444, #b91c1c)',
                  color: '#fff', cursor: deleting ? 'default' : 'pointer', opacity: deleting ? 0.7 : 1,
                }}
              >
                {deleting ? t('myGames.deleting') : <><span aria-hidden="true">🗑 </span>{t('common.delete')}</>}
              </button>
            </div>
          </div>
        </Dialog>
      )}
    </div>
  );
}
